import { faCloudSunRain, faUmbrella, faWind } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Result } from "antd";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import React, { useCallback, useMemo } from "react";
import Helper from "../../Helper";
import Weather from "../../Weather";
import { DailyForecast, HourlyForecast } from "../WeatherResponseTypes";

dayjs.extend(localizedFormat);

interface Props {
    textColor?: string;
    flexDirection: string;
    headerTextColor?: string;
    headerFontSize?: string;
    weather: any;
    project: any;
}

const UpcomingHourly: React.FC<Props> = (props) => {
    const isMobile = Helper.isMobileDevice();

    const [hourly, setHourly] = React.useState<HourlyForecast[]>([]);
    const [daily, setDaily] = React.useState<DailyForecast[]>([]);

    const todayLow = daily?.[0]?.low_temp ?? 0;
    const todayHigh = daily?.[0]?.high_temp ?? 0;

    const hasData = useMemo(() => hourly?.length > 0 && daily?.length > 0, [hourly, daily]);

    React.useEffect(() => {
        setHourly(props.weather.hourly_forecast);
        setDaily(props.weather.daily_forecast);
    }, [props.weather]);

    const getMarginRight = useCallback(
        (temp: number) =>
            Weather.getRightMarginPercentage(
                todayHigh, // max
                todayLow, // min
                temp
            ),
        [todayHigh, todayLow]
    );

    if (!hasData) {
        return (
            <Result
                className={Helper.classNames("w-full h-full")}
                icon={<FontAwesomeIcon icon={faCloudSunRain} size="3x" />}
                title={<div className={props.textColor}>Weather currently unavailable</div>}
            />
        );
    }

    return (
        <div
            className={`flex items-center w-full h-full justify-between my-2 ${
                props.flexDirection.includes("row") ? "flex-wrap" : "flex-nowrap"
            }`}
        >
            {/* Header */}
            <div
                className={Helper.classNames(
                    "flex justify-between items-center font-plot-oswald text-center uppercase",
                    props.headerTextColor ? `text-[${props.headerTextColor}]` : "text-white",
                    props.flexDirection,
                    props.flexDirection.includes("row") && "w-full"
                )}
            >
                <div
                    className={Helper.classNames(
                        "flex items-center justify-center basis-1/6",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                >
                    Hourly
                </div>
                <div
                    className={Helper.classNames(
                        "flex items-center justify-center basis-1/12",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                />
                <div
                    className={Helper.classNames(
                        "flex basis-1/6 grow w-full flex-row justify-between",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                >
                    <div>{Math.round(todayLow)}°</div>
                    <div>{Math.round(todayHigh)}°</div>
                </div>
                <div
                    className={Helper.classNames(
                        "flex items-center justify-center basis-1/6",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                >
                    <FontAwesomeIcon icon={faUmbrella} />
                </div>
                <div
                    className={Helper.classNames(
                        "flex basis-1/6 items-center text-center justify-center whitespace-nowrap font-plot-oswald uppercase",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                >
                    <FontAwesomeIcon icon={faWind} className={Helper.classNames(!isMobile && "mr-1")} />
                    {!isMobile && "Sust. / Gust"}
                </div>
            </div>

            {hourly.slice(0, 5).map((hour, index) => (
                <div
                    key={hour.time ?? index}
                    className={`flex justify-between items-center ${props.flexDirection} ${
                        props.flexDirection.includes("row") && "w-full"
                    }`}
                >
                    <div className="flex flex-row items-center justify-center whitespace-nowrap basis-1/6">
                        {dayjs(hour.time).format("LT")}
                    </div>

                    <div className="flex items-center justify-center basis-1/12">
                        <FontAwesomeIcon icon={Weather.getWeatherIcon(hour.condition)} size={isMobile ? "sm" : "1x"} />
                    </div>

                    <div className="flex basis-1/6 grow flex-row justify-between font-plot-oswald text-lg text-center uppercase relative w-full h-1 bg-linear-to-r from-blue-500 to-red-500">
                        <div
                            style={{ marginRight: getMarginRight(hour.temperature) }}
                            className={Helper.classNames("z-10 right-0 absolute bottom-[-300%]")}
                        >
                            <div className="w-6 h-6 border-solid border-2 text-white border-white bg-[#222] text-sm rounded-full flex items-center justify-center">
                                {Math.round(hour.temperature)}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-row items-center justify-center basis-1/6">
                        {hour.precipitation_chance ?? 0}%
                    </div>

                    <div className="flex flex-row items-center justify-center whitespace-nowrap basis-1/6">
                        <div className="flex items-center gap-1 font-roboto-mono">
                            <span className="inline-block w-4 text-right">{Math.round(hour.wind_speed ?? 0)}</span>
                            <span>/</span>
                            <span className="inline-block w-4 text-left">{Math.round(hour.wind_gust ?? 0)}</span>
                            <span className="ml-1">mph</span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default UpcomingHourly;
